import { callApi, ThrowError } from '@/api/callApi';
import { API_ENDPOINT, API_URL, API_URL_USER_SERVICE } from '@/api/constant';
import ErrorMessage from '@/components/common/ErrorMessage';
import AuthInputField from '@/components/UI/AuthInputField';
import { svgIcons } from '@/components/UI/SvgIcons';
import { requestForToken } from '@/firebase-messaging';
import { ILoginResponse } from '@/interfaces/loginResponse.interface';
import { IRole, IUser } from '@/interfaces/user.interface';
import { setUserData } from '@/redux/features/UserSlice';
import { useAppDispatch } from '@/redux/store';
import {
  getDeviceId,
  setCookies,
  showToastNotification,
} from '@/utility/helper';
import { RoutesLink } from '@/utility/RouteLinks';
import { errorTypes } from '@/utility/validation';
import { LoaderCircle } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router-dom';
const { InfoIcon, EmailIcon, EyeCloseIcon } = svgIcons;

interface LoginFormValues {
  roleId: string;
  email: string;
  password: string;
}

const Login: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [roles, setRoles] = useState<IRole[]>([]);
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginFormValues>({
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const [fcmToken, setFCMToken] = useState<string | null>(null);

  useEffect(() => {
    getFirebaseData();
  }, []);

  const getFirebaseData = async () => {
    const token = await requestForToken();
    setFCMToken(token);
  };

  const fetchRoles = async () => {
    try {
      const PATH = API_URL + API_ENDPOINT.roles;
      const roles = await callApi<IRole[]>(PATH, { method: 'GET' });
      setRoles(roles);
    } catch (error: any) {
      // console.log(error);
      showToastNotification('danger', error.message);
    }
  };

  useEffect(() => {
    fetchRoles();
  }, []);

  const onSubmit = async (data: LoginFormValues) => {
    try {
      const PATH = `${API_URL + API_ENDPOINT.auth}/login`;
      const postData = {
        roleId: data.roleId,
        identifier: data.email,
        deviceId: getDeviceId(),
        deviceToken: fcmToken,
        password: data.password,
      };
      const res = await callApi<ILoginResponse>(PATH, {
        method: 'POST',
        data: postData,
      });
      setCookies(`${import.meta.env.VITE_BUSINESS}token`, res.accessToken);
      setCookies(
        `${import.meta.env.VITE_BUSINESS}refreshToken`,
        res.refreshToken,
      );
      if (fcmToken) {
        setCookies(`${import.meta.env.VITE_BUSINESS}deviceToken`, fcmToken);
      }
      const userDetail = await callApi<IUser>(
        `${API_URL_USER_SERVICE}${API_ENDPOINT.users}/detail`,
        { method: 'GET' },
      );

      dispatch(setUserData(userDetail));
      navigate('/dashboard');
    } catch (error) {
      ThrowError(error)
    }
  };

  return (
    <div className="mx-auto min-h-screen relative overflow-hidden">
      <div className="relative grid lg:grid-cols-2 items-center">
        <div className="relative h-dvh blur-sm lg:blur-none">
          <video
            className="w-full h-full object-cover"
            autoPlay
            muted
            loop
            playsInline
          >
            <source src="./assets/videos/login-video.mp4" type="video/mp4" />
          </video>
          <span className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
            <img src="./assets/images/white-logo.png" alt="" />
          </span>
        </div>
        <div className=" w-[calc(100%-40px)] lg:h-full  lg:w-full lg:relative flex items-center justify-center absolute bg-white/95 lg:bg-transparent -translate-x-1/2 -translate-y-1/2 top-1/2 left-1/2 rounded-lg">
          <img
            className="h-full w-full left-0 top-0 absolute object-cover"
            src="./assets/images/login-bg-gradient.png"
            alt=""
          />
          <div className="p-8 xl:p-10 relative max-h-dvh overflow-auto">
            <div
              className="w-full mx-auto max-w-[500px]"
              uk-scrollspy="target: > *; cls: uk-animation-scale-up; delay: 100 ;repeat: true"
            >
              <h2 className="text-2xl lg:text-4xl mb-4">
                Welcome to Nass Foods
              </h2>
              <p className="text-sm sm:text-base mb-5 lg:mb-12">
                Secure access to your dashboard. Manage users, track activities,
                and oversee operations with ease
              </p>
              <h3 className="font-semibold mb-5 lg:mb-8 sm:text-center capitalize text-lg sm:text-3xl">
                Log In
              </h3>
              <form onSubmit={handleSubmit(onSubmit)}>
                <div className="relative mb-4">
                  <label htmlFor="roleId" className="form-label">
                    Select your role
                  </label>
                  <div className="relative">
                    <AuthInputField
                      type="select"
                      options={roles.filter(r => r.name !== 'User').map((r) => ({
                        value: r.id,
                        label: r.name,
                      }))}
                      placeholder="Select a role"
                      {...register('roleId', {
                        ...errorTypes.required('role'),
                      })}
                    />
                  </div>
                  <ErrorMessage message={errors.roleId?.message} />
                </div>
                {/* e.g. sajin hentry@gmail.com */}
                <div className="relative mb-4">
                  <label htmlFor="email" className="form-label">
                    Email
                  </label>
                  <div className="relative">
                    <AuthInputField
                      placeholder="e.g. sajin hentry@gmail.com"
                      {...register('email', {
                        ...errorTypes.required('Email'),
                        ...errorTypes.email,
                      })}
                    />
                    <span className="absolute right-1 top-1/2 -translate-1/2 cursor-pointer">
                      {EmailIcon}
                    </span>
                  </div>
                  {errors.email && (
                    <div className="form-error text-red-600 flex items-center gap-1 text-xs mt-2">
                      {InfoIcon}
                      {errors.email.message}
                    </div>
                  )}
                </div>

                {/* Password */}
                <div className="mb-4">
                  <div className="relative">
                    <label htmlFor="password" className="form-label">
                      Password
                    </label>
                    <div className="relative">
                      <AuthInputField
                        type={showPassword ? 'text' : 'password'}
                        placeholder="Password"
                        {...register('password', {
                          ...errorTypes.required('Password'),
                        })}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword((prev) => !prev)}
                        className="absolute right-1 top-1/2 -translate-1/2 cursor-pointer"
                      >
                        {EyeCloseIcon}
                      </button>
                    </div>
                  </div>
                  {errors.password && (
                    <div className="form-error text-red-600 flex items-center gap-1 text-xs mt-2">
                      {InfoIcon}
                      {errors.password.message}
                    </div>
                  )}
                </div>
                <div className="flex items-center justify-end mb-5 sm:mb-10">
                  <Link to={RoutesLink.forgetPassword} className="text-gray-100 capitalize">
                    Forgot your password?
                  </Link>
                </div>
                <button
                  disabled={isSubmitting}
                  type="submit"
                  className="btn-primary w-full"
                >
                  {isSubmitting ? (
                    <LoaderCircle
                      strokeWidth={2.5}
                      className="w-5 h-5 animate-spin text-white"
                    />
                  ) : (
                    'Log in'
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
